import type { ConsentStatus } from "../../../shared/api/parent";

/**
 * C1–C5's copy.
 *
 * ⚠️ THIS TABLE HAD A MALAYALAM COLUMN (ADR-044). The parent tier is the one
 * read on a **phone**, and `User.language` (P6) still exists — a parent's
 * language is independent of their school's default, there is simply one value
 * it can take.
 *
 * 🔴 THE HARDEST SENTENCES IN THIS FILE ARE THE ONES ABOUT RIGHTS, and they are
 * hard because they have to be *true*. Revoking consent does not delete
 * anything; erasure keeps the child on the school's roster; the export is not a
 * report. Each of those is a place where the comfortable wording would be a lie.
 *
 * 🔴 EVERY CHILD IS "THEY", AND THIS IS NOT A STYLE PREFERENCE. These strings
 * said "she" — seventeen of them — so a parent opening the app read *"What she
 * is learning"* above their son's name, and *"Yes, keep her progress"* on the
 * consent button. The product holds no gender for a child and should not: ADR-004
 * keeps a Player free of PII, and a roster import carries a name and a roll
 * number. There is nothing to derive a pronoun FROM, so the copy had picked one,
 * and it was wrong for roughly half the children in every classroom — on the
 * screen where a school asks a family to trust it with their child's record.
 *
 * ⚠️ IT WAS ONLY EVER AN ENGLISH PROBLEM. The second language had no pronoun
 * to get wrong here — it named the child where English reached for "he" or
 * "she", which is what English does now too wherever "they" would read
 * awkwardly.
 */

export type Lang = "en";

export const PARENT_COPY = {
  en: {
    /* C1 */
    dashboard: "Overview",
    /*
     * ⚠️ WHAT THE THREAD IS TITLED WHEN THE SERVER SENDS NO NAME.
     * `MessageThread.otherSide` is nullable — a class with no teacher assigned
     * is a real state — and the screen used to fall back to an invented
     * "Ms. Priya Sharma". A role is true whoever holds it; a name is not.
     */
    teacherFallback: "Your child’s teacher",
    /*
     * ⚠️ IT INVITES RATHER THAN APOLOGISES. An empty thread is the normal state
     * for most families most of the time, and "no messages" read as a fault
     * would send parents to a school asking why the app is broken.
     */
    threadEmpty:
      "No messages yet. You can write to your child’s teacher here, and their replies will appear in this thread.",
    switchChild: "Choose a child",
    noChildren:
      "No children are linked to this number yet. Your school links them from the roster.",
    gamesThisWeek: "Games this week",
    sessionsThisWeek: "Sessions this week",
    learningTime: "Time playing",
    /*
     * ── The four tile captions ──
     *
     * 🔴 THEY ARE DELIBERATELY NOT THE SAME WORDS. Two of the tiles on that row
     * are weekly, one is an all-time running total and one is a floor over a
     * window the SERVER chooses. The row used to caption three of them "This
     * Week", which made the coin total — a number that only ever goes up, over
     * the child's whole life on the product — read as a week's earnings.
     */
    captionThisWeek: "This week",
    captionAllTime: "All time",
    /* ⚠️ "AT LEAST", because `ActivityDay.minutes` is a floor: a sitting
       nothing ever closed contributes nothing to it. And the window is the one
       the answer states, never the one the screen asked for. */
    captionAtLeast: (days: number) =>
      `At least, over ${String(days)} days`,
    streak: "Days in a row",
    coins: "Coins",
    lastPlayed: "Last played",
    neverPlayed: "Not started yet",
    contentSkills: "What they are learning",
    /**
     * 🔴 THE SENTENCE THAT TURNS FIVE BARS INTO FIVE GAMES. Every card in this
     * section now carries the plate the child taps to start it, and this says
     * why — a parent who reads "Sky Quest" here can ask about it at dinner,
     * which is more than "Numeracy, level 2" has ever given them.
     */
    contentSkillsHint:
      "One game teaches each of these. The picture is what your child taps.",
    motorSkills: "How they are moving",
    /** 🔴 §3 — motor skills are earned across ALL five games, so no one plate is
        honestly their picture, and this says so rather than showing a bar with
        no source. */
    motorSkillsHint: "Earned across all five games, not taught by one.",
    /** 🔴 The template `SkillProgress` fills — content skills read as a level. */
    levelTemplate: "Level {level} of {of}",
    notStarted: "Not started",
    insight: "This week",

    /*
     * ─────────────────────────────────────────────────── coins and stickers ──
     *
     * 🔴 THE TWO NUMBERS A CHILD ACTUALLY TALKS ABOUT, AND THE PRODUCT NEVER
     * EXPLAINED EITHER. C1 showed "142 coins" and "7 stickers" as bare counts and
     * C3 showed a row of pills reading `sky-quest-l2`. A parent asked "how did
     * they get 142?" had nowhere to look, and the honest answer is short: coins
     * are right answers multiplied by the level, and a sticker is a round they
     * got most of.
     *
     * 🔴 THE ARITHMETIC IS STATED BECAUSE IT IS CHECKABLE. `rewards.ts` is
     * `coins: correct * level` — so a parent CAN work out a round, and the one
     * thing worse than not explaining it would be explaining it wrongly.
     * `stickerBook.test.ts` asserts this copy and that function still agree.
     */
    rewards: "Coins and stickers",
    rewardsHint: "What your child has collected so far.",
    /*
     * 🔴 THE TILES ARE TILES AGAIN, AND THE ESSAY THAT WAS INSIDE THEM IS GONE.
     *
     * Each of the three carried its label as a QUESTION — "What coins are" —
     * over a paragraph, a worked sum and a note: about ninety words stacked
     * under three numbers, in cards the width of the screen. The same three
     * figures on the teacher's Leaderboard are a label, a numeral and four
     * words, and a parent moving between the two tiers met a different design
     * for the same fact.
     *
     * ⚠️ WHAT THE PARAGRAPHS SAID IS NOT WRONG, AND IS NOT LOST — it is recorded
     * in `Rewards.tsx` and in `stickerBook.ts`, whose `coinsForRound` is still
     * the arithmetic `rewards.ts` runs and is still held to it by a test. If the
     * explainer comes back it belongs in a section of its own, under the tiles,
     * not inside them.
     */
    coinsWhat: "Coins",
    coinsCaption: "Earned by playing",
    stickersWhat: "Stickers",
    stickersCaption: "Kept forever",
    /** A sticker, read back as the thing it was earned for. */
    stickerFor: (game: string, level: number) =>
      `${game} · Level ${String(level)}`,
    stickerShelf: "Their sticker shelf",
    stickerShelfEmpty:
      "No stickers yet. The first one arrives on a strong round.",
    /** 🔴 A sticker this build cannot name is still a sticker they earned. */
    stickerUnknown: "From a game this app does not know yet",
    stickerCountFor: (n: number, game: string) =>
      n === 1 ? `1 sticker from ${game}` : `${String(n)} stickers from ${game}`,
    streakWhat: "Days in a row",
    streakCaption: "At least one game a day",

    recentActivity: "Recent activity",
    /*
     * ⚠️ C2's ACTIVITY TABLE SHIPPED WITH NO HEADER ROW AT ALL — three
     * unlabelled columns, and a screen reader announcing a grid with nothing to
     * associate the cells with. These are the three it always had.
     */
    date: "Date",
    game: "Game",
    skill: "Skill",

    /*
     * ────────────────────────────────────────────────── the activity feed ──
     *
     * ✅ THE THREE-COLUMN TABLE BECAME A DAY-BY-DAY TIMELINE, and these are the
     * strings that carry the change. The table printed one row per EVENT — a
     * date, a game and "NUMERACY L2" — so a single afternoon filled the screen
     * with forty near-identical lines and a parent scrolled past their child's
     * week without seeing a day in it. Grouping by day is not decoration: the
     * question a parent asks is "what did they do on Tuesday", and that is a day,
     * not an event.
     */
    activityHint:
      "Grouped by day, newest first. Each day shows what they opened and how it went.",
    activityEmpty: "Nothing recorded yet. It fills up as they play.",
    /** The heading on a day's card. `today` and `yesterday` are worth the special case. */
    activityToday: "Today",
    activityYesterday: "Yesterday",
    activityRounds: (n: number) =>
      n === 1 ? "1 activity" : `${String(n)} activities`,
    activitySessions: (n: number) =>
      n === 1 ? "1 sitting" : `${String(n)} sittings`,
    /** 🔴 "At least" — a sitting nothing closed contributes nothing. See the API type. */
    activityMinutes: (n: number) => `at least ${String(n)} min`,
    activityAnswers: (right: number, of: number) =>
      `${String(right)} of ${String(of)} answers right`,
    activityLevelUps: (n: number) =>
      n === 1 ? "1 level mastered" : `${String(n)} levels mastered`,
    activityLevel: (n: number) => `Level ${String(n)}`,
    /** A sitting with no events at all — I5, or a child who opened the app and left. */
    activityOpenedOnly: "Opened, nothing recorded",

    /*
     * C6 — attendance.
     *
     * 🔴 NOT ONE OF THESE STRINGS IS A PERCENTAGE, AND THAT IS THE CONTRACT THE
     * SERVER KEEPS TOO. `Absence` stores absences; a day when everybody came
     * writes no row, so "92% attendance" would need a denominator nobody has.
     * The screen says which days instead — a parent can ask about a Tuesday.
     */
    attendance: "Attendance",
    attendanceWindow: (days: number) => `Last ${String(days)} days`,
    attendanceAbsentDays: "Days absent",
    attendanceNone: "No absences in this period.",
    attendanceNeverAbsent: "Never marked absent.",
    attendanceLast: "Last absence",
    /*
     * 🔴 THE SENTENCE THAT STOPS A PARENT READING A BLANK SCREEN AS A CLEAN
     * RECORD. A teacher who never opened the register writes no rows, and that
     * is indistinguishable in the data from perfect attendance. Saying so is the
     * difference between an honest screen and a reassuring one.
     */
    attendanceHint:
      "Only days your child was marked absent appear here. If the register was not taken, the day is not shown at all.",
    attendanceAsk: "Ask the school if a day here looks wrong.",
    attendanceOverview: "Attendance overview",
    /*
     * ⚠️ "AT LEAST", BECAUSE `ActivityDay.minutes` IS A FLOOR. A sitting nothing
     * ever closed contributes nothing to it, so a flat "12 min" would understate
     * a day and read as a measurement.
     */
    dayPlaySummary: (sessions: number, minutes: number) =>
      minutes > 0
        ? `Played ${String(sessions)} ${sessions === 1 ? "time" : "times"}, at least ${String(minutes)} min`
        : `Played ${String(sessions)} ${sessions === 1 ? "time" : "times"}`,
    attendanceRecent: "Recent attendance",
    attendanceColDate: "Date",
    attendanceColStatus: "Status",
    attendanceColRemarks: "What else happened",
    attendanceNoRows: "Nothing recorded in this window yet.",
    /*
     * 🔴 THE DENOMINATOR IS IN THE SENTENCE, NOT IMPLIED BY IT.
     *
     * "Present 96% of the time" is the claim `AttendanceDay` refuses — a rate
     * with no visible base, about somebody's child. Naming the window makes the
     * same number true, and naming the unmarked days stops the percentage
     * quietly absorbing them: a school that never opened the register would
     * otherwise read as a school where every child was present.
     */
    attendanceSummary: (
      name: string,
      pct: number,
      total: number,
      noRecord: number,
    ) =>
      noRecord === 0
        ? `${name} was marked present on ${String(pct)}% of the last ${String(total)} days.`
        : `${name} was marked present on ${String(pct)}% of the last ${String(total)} days. The register was not taken on ${String(noRecord)} of them.`,
    /*
     * ⚠️ ADVICE, KEPT GENERAL AND KEPT SHORT. It is the one thing on this screen
     * that is not a fact about this child, so it must not read as one — no
     * number in it, and nothing that could be mistaken for the school speaking.
     */
    attendanceTipTitle: "Why it matters",
    attendanceTipBody:
      "Regular attendance helps children settle into routines and build learning habits. If your child has to miss school, telling the class teacher early helps them catch up.",
    /*
     * 🔴 THE LEGEND IS WHERE THE CALENDAR STAYS HONEST. An unmarked square is
     * "no absence recorded" and NOT "present" — a register nobody took writes no
     * row, which is identical in the data to a day the whole class came in. A
     * grid of green ticks would be the reassuring version of a screen whose
     * whole purpose is to be the truthful one.
     */
    attendanceLegendAbsent: "Marked absent",
    /**
     * 🔴 "THE REGISTER SAID THEY WERE THERE" — NOT "they attended all day".
     * `RegisterDay` records that a teacher pressed save and this child was not
     * on the absent list. That is a real fact and it is a narrower one than
     * attendance, which is why the word "register" is in the sentence.
     */
    attendanceLegendPresent: "The register marked them in",
    attendanceLegendHoliday: "School closed",
    /**
     * 🔴 NOT "ABSENT", AND NOT "PRESENT". Nobody saved a register that day, so
     * the honest answer is that nothing is known — and this is the row that
     * keeps the other three meaningful.
     */
    attendanceLegendUnknown: "No register taken",
    /** The accessible name on a marked square, and its tooltip. */
    attendanceAbsentOn: (day: string) => `Marked absent on ${day}`,
    attendancePresentOn: (day: string) =>
      `${day} — the register marked them in`,
    attendanceHolidayOn: (day: string, name: string) =>
      `${day} — school closed for ${name}`,
    attendanceUnknownOn: (day: string) => `${day} — no register was taken`,
    /** Absences the 28-square grid cannot reach — see `attendanceCalendar.ts`. */
    attendanceEarlier: "Also marked absent, just before this window:",

    /*
     * ───────────────────────────────────────────────── one day on the calendar ──
     *
     * 🔴 THE CALENDAR INVITED A TAP AND ANSWERED NOTHING. Twenty-eight squares is
     * a control shaped like a button, and the only thing the product could say
     * about a day was whether somebody had marked the child absent — which the
     * square's own colour already said. "What did they actually do on Tuesday" is
     * the question a calendar makes a parent ask, and it now has an answer.
     *
     * ⚠️ THE TWO HALVES ARE FROM DIFFERENT TABLES AND THE COPY KEEPS THEM APART.
     * The register is the school's record of attendance; the games are the
     * child's own play, which happens at home as often as at school. A day that
     * says "no register taken" and "played Sky Quest for 12 minutes" is not a
     * contradiction, and a parent should not read it as one.
     */
    dayPick: "Tap a day to see what happened",
    dayTitle: (day: string) => `What happened on ${day}`,
    dayClose: "Close",
    /** The register's half, when the day carries no play at all. */
    dayNoPlay: "No games recorded on this day.",
    dayNoPlayHint:
      "This is about the games, not about school. A day with no games is an ordinary day.",
    daySchoolSaid: "What the school recorded",
    dayTheyPlayed: "What they played",
    /** 🔴 The future is not an empty day. */
    dayNotYet: "This day has not happened yet.",

    /*
     * ──────────────────────────────────────────────────────────── the numbers ──
     *
     * 🔴 EVERY RATIO ON THIS SCREEN IS PRINTED AS ITS DIVISION. OD-39 and
     * contract D4: a percentage a parent cannot check is a measurement claim
     * about their child, and the first question a school asks is "percent of
     * what?". So the copy is built from templates that take BOTH numbers — there
     * is deliberately no string here that formats a bare percentage.
     *
     * 🔴 AND THERE IS NO TARGET, NO BENCHMARK AND NO OTHER CHILD ON THIS SCREEN.
     * Every chart compares this child with themselves over the window, which is
     * the only comparison the data supports. A "class average" would be an
     * invented denominator with somebody else's child in it.
     */
    numbers: "The numbers",
    numbersHint:
      "Everything here is counted from what your child actually did in the last {days} days. There is no target and no comparison with other children.",
    numbersEmpty: "Nothing to count yet. These fill in once your child plays.",
    /** ⚠️ Shown only if the server hit its runaway guard. Honest rather than silent. */
    numbersTruncated:
      "There was more activity than this page can add up, so these totals are a floor rather than the full picture.",

    metricAccuracy: "Answers they got right",
    metricAccuracyOf: (right: number, of: number) =>
      `${String(right)} right out of ${String(of)} answered`,
    metricAccuracyNone: "No questions answered yet",
    metricDaysPlayed: "Days they played",
    metricDaysPlayedOf: (played: number, of: number) =>
      `${String(played)} of the last ${String(of)} days`,
    metricMinutes: "Time playing",
    /** 🔴 "At least" is load-bearing — see `ActivityDay.minutes`. */
    metricMinutesValue: (n: number) => `at least ${String(n)} min`,
    metricMinutesPerDay: (n: number, days: number) =>
      `about ${String(n)} min on each of the ${String(days)} days they played`,
    metricLevels: "Levels mastered",
    metricLevelsHint:
      "A level counts once, the first time they finish it well.",
    metricBestRun: "Longest run",
    metricBestRunValue: (n: number) =>
      n === 1 ? "1 day" : `${String(n)} days in a row`,
    metricSessions: "Sittings",
    metricBusiest: (day: string, n: number) =>
      `Their busiest day was ${day} — ${String(n)} activities`,

    chartActivity: "Day by day",
    chartActivityHint:
      "One bar per day. The tallest is your child’s own busiest day in this period, not a target.",
    chartActivityMax: (n: number) => `Tallest bar: ${String(n)} activities`,
    chartWeekday: "Which days they play",
    chartWeekdayHint:
      "Averaged per weekday, because a period does not hold the same number of each. “2 of 4 Mondays” means they played on two of the four Mondays here.",
    chartWeekdayOf: (played: number, of: number) =>
      `${String(played)} of ${String(of)}`,
    chartSkills: "What the time went on",
    chartSkillsHint:
      "Share of everything they did that a skill can be named for.",
    chartSkillsOf: (n: number, of: number) =>
      `${String(n)} of ${String(of)} activities`,

    /*
     * ───────────────────────────────────────────────────────── the PDF report ──
     *
     * 🔴 IT IS A DOCUMENT A PARENT WOULD SHOW SOMEBODY, WHICH IS A REAL
     * REQUIREMENT AND NOT A FLOURISH. The button already existed and printed the
     * screen — a sidebar, a disabled button and a half-cropped chart — so the
     * file it produced looked like a screenshot of an app, and nobody sends that
     * to a grandparent. These strings belong to a report sheet drawn only for
     * print: a title, the period it covers, and every number with its
     * denominator, exactly as the screen states them.
     *
     * ⚠️ NOTHING IN THE REPORT IS A GRADE, AND NOTHING IS COMPARED WITH ANOTHER
     * CHILD. It is generous about what the child did and silent about what they
     * did not — because the honest version of this document is already something
     * to be proud of, and an invented ranking would be the one thing that makes
     * it untrue.
     */
    reportTitle: "Progress report",
    reportSubtitle: "What they have been learning",
    reportPeriod: (from: string, to: string) => `${from} — ${to}`,
    reportGenerated: (at: string) => `Printed ${at}`,
    reportClass: "Class",
    reportChild: "Child",
    reportHighlights: "The headlines",
    reportLearning: "What they are learning",
    reportMoving: "How they are moving",
    reportRewards: "What they have collected",
    reportDays: "Their days",
    reportThisWeek: "This week",
    /*
     * The per-question summary on the sheet. Counts only, each with its
     * denominator; a wrong answer is "still learning", and a Turo pose or an
     * unanswered question is never one.
     */
    reportAreas: "Questions in each area",
    reportAreaQuestions: "Questions answered",
    reportAreaFirstTry: "Right first time",
    reportAreaStillLearning: "Still learning",
    reportAreaNotPlayed: "Not played in this period",
    reportAreasTotal: (n: number) =>
      `${n.toLocaleString("en-IN")} ${n === 1 ? "question" : "questions"} answered across the five areas.`,
    reportPoses: (line: string) => `Poses copied: ${line}`,
    reportAnswering: "How they answered",
    reportFooter:
      "Made by Kalqy from what your child did in the games. Every number here is a count of real activity — there are no estimates, no grades and no comparison with other children.",
    reportNoData: "There is nothing recorded for this period yet.",
    printHint:
      "Opens your browser’s print window — choose “Save as PDF” to keep a copy.",

    /* C2 */
    progressDetail: "Progress detail",
    /**
     * 🔴 IT PRINTS THE PAGE. There is no server-side PDF: OD-59 refused a
     * rendering dependency on a route that emits children's names, with no
     * specified layout, and puppeteer would put headless Chromium on a
     * Lightsail node sized on cost grounds.
     */
    printReport: "Print or save as PDF",
    messageTeacher: "Message the teacher",
    /**
     * ✅ THE SENTENCE THIS TIER HAS SHIPPED SINCE IT WAS BUILT, AND IT IS NO
     * LONGER TRUE. The button was disabled with its reason — the D5 precedent,
     * *"a parent who cannot find the feature complains to the school, one who
     * can see why it is greyed out does not"* — and the reason was that §9.2
     * owned the only send path, which is a WhatsApp template. The thread exists
     * now, so the button opens it. The key stays because the string is the
     * record of what the screen used to say.
     */
    messageTeacherDisabled: "Available once parent messaging is switched on",

    /*
     * ── the conversation ────────────────────────────────────────────────────
     *
     * 🔴 A PARENT COULD RECEIVE A PRE-APPROVED TEMPLATE AND COULD NOT REPLY TO
     * IT. Every message this product has ever sent a family is a WhatsApp
     * template whose body was submitted to Meta weeks in advance, one-way — so
     * *"she left her water bottle at home, could you remind her"* had no path at
     * all, and the school's answer was a phone call.
     */
    messagesThread: "Messages with the teacher",
    messagesThreadHint: "About {child}. The class teacher sees these.",
    messagesEmpty:
      "No messages yet. Write to the teacher and it appears on their screen.",
    messagesNoTeacher:
      "No teacher is assigned to this class yet. What you write is kept and appears as soon as the school assigns one.",
    messageWrite: "Write a message",
    messageSend: "Send",
    /** ⚠️ "Seen", never "read" — the server can only know the thread was opened. */
    messageSeen: "Seen",
    messageYou: "You",
    messagesUnread: (n: number) =>
      n === 1
        ? "1 new message from school"
        : `${String(n)} new messages from school`,

    /* C3 */
    achievements: "Achievements",
    stickersEarned: "Stickers",
    noAchievements: "No stickers yet. They arrive as they play.",

    /*
     * C4 — the DELIVERY LOG, not the conversation.
     *
     * ⚠️ IT USED TO BE HEADED "Messages", AND THAT WAS THE BUG (ADR-052). This
     * screen lists what the product SENT — weekly reports, by WhatsApp or in the
     * app — and it sat under the nav item a parent taps to read what the TEACHER
     * wrote. So a teacher's message could arrive and the parent be told "No
     * messages yet" on a screen with the same name. The thread is drawn above
     * this now, and this heading says which of the two it is.
     */
    notifications: "What we have sent you",
    channelWhatsapp: "WhatsApp",
    channelInApp: "In the app",
    noNotifications:
      "Nothing sent yet. Weekly reports start once messaging is switched on for your number.",

    /*
     * ───────────────────────────────────────────────────────── announcements ──
     *
     * 🔴 THE ONE THING A SCHOOL DOES EVERY WEEK AND THIS PRODUCT COULD NOT DO AT
     * ALL. A school with something to say — sports day is Friday, the bus route
     * changed, fees are due — had a holiday calendar, a homework form and nowhere
     * to say it. Parents heard it from other parents, or did not.
     *
     * ⚠️ THE COPY NEVER PROMISES DELIVERY. A notice arrives on this screen, and
     * on WhatsApp if the school asked for that and the parent has not opted out.
     * "We told everyone" is not a claim this product can make, so it does not.
     */
    announcements: "Announcements",
    announcementsHint:
      "Notices from your child’s school. The newest is at the top.",
    noAnnouncements:
      "Nothing from the school yet. Notices appear here as soon as they are sent.",
    announcementFrom: (school: string) => `From ${school}`,
    /** The badge on a notice that arrived while this screen was open. */
    announcementNew: "Just arrived",
    /** C1's strip. Deliberately not the whole feed — see `ParentDashboardScreen`. */
    announcementsLatest: "From the school",
    announcementsSeeAll: "See all notices",
    announcementsLive: "Live",
    announcementsLiveHint: "New notices appear here without reloading.",

    /* C5 */
    settings: "Settings",
    /**
     * 🔴 THE SUBTITLE SAYS WHICH SETTINGS THESE ARE, because this screen is now
     * the only place the word appears for a parent. It used to sit in the nav
     * bar as "Alerts" while the shell's Settings control opened a staff password
     * form one corner away — two names, two screens, one of them useless here.
     */
    settingsIntro: "What we send you, and what is kept about your child.",
    /* ── change 19 §8 — the way in to the platform survey ─────────────────── */
    feedbackTitle: "How is Kalqy going for you?",
    /* 🔴 THE ANONYMITY IS THE OFFER, AND IT IS TRUE. `submitFeedback` sends no
       token and the row records no parent, no child and no school — so this line
       is a fact about the code and not a reassurance. A parent who thinks their
       name is attached answers differently, or not at all. */
    feedbackHint:
      "Tell us what is working and what is not. It is anonymous — nothing you send is linked to you or to your child.",
    feedbackOpen: "Give feedback",

    accountSettings: "For this account",
    childSettings: "For this child",

    /**
     * 🔴 THE ANSWER TO "WHERE DO I CHANGE MY PASSWORD" IS THAT THERE IS NOT ONE,
     * AND A SETTINGS SCREEN THAT STAYS SILENT ABOUT IT IS THE ONE A PARENT
     * SEARCHES TWICE. §9.1 signs a parent in with a phone number and a one-time
     * code; `/auth/login` refuses PARENT and `changeOwnPassword` refuses a row
     * with no hash, so the account genuinely has no password — not a hidden one,
     * not one an admin holds.
     */
    signInSection: "How you sign in",
    signInHow:
      "You sign in with your phone number and a code we send you. There is no password on this account, so there is nothing here to change and nothing to forget.",
    /* ⚠️ THE NUMBER COMES FROM THE SCHOOL'S REGISTER (`roster.service.ts` creates
       the parent from it), so this points at the people who can actually change
       it rather than offering a control that would have to refuse. */
    signInNumber:
      "If your number changes, tell your child’s school — the number we sign you in with is the one on their register.",

    /* G3's switch, for the one tier that no longer passes through G3. */
    deviceSettings: "On this phone",
    reduceMotion: "Reduce movement",
    reduceMotionHelp:
      "Turns off the movement and animation on these screens. It is saved on this phone rather than on your account.",
    language: "Language for my messages",
    languageFollowSchool: "Follow my school",
    whatsappOptIn: "Send me WhatsApp messages",
    quietHours: "Do not message me between",
    quietHoursOff: "No quiet hours set",
    quietFrom: "From",
    quietTo: "Until",
    quietWrapsMidnight: "This window runs overnight.",
    messageKinds: "What to send me",
    kindWeeklyReport: "Weekly report",
    kindAchievement: "When they earn something",
    kindReminder: "Reminders",
    kindSystem: "Account and service notices",
    frequency: "How often",
    frequencyImmediate: "Straight away",
    frequencyDaily: "Once a day",
    frequencyWeekly: "Once a week",
    frequencyOff: "Never",
    inherited: "Using the account setting",
    save: "Save",
    saved: "Saved",

    /* P4 */
    recordingOptOut: "Do not keep records of this child’s sessions",
    recordingOptOutHelp:
      "They can still play everything. Turning this on stops us keeping what they did, so their progress here will stop updating.",

    /* P3 — consent */
    consentTitle: "Your child’s data",
    consentGranted: "You have given consent",
    consentPending: "Consent has not been given yet",
    consentRevoked: "You withdrew consent",
    consentExpired: "Consent has run out",
    consentPendingHelp:
      "Nothing about this child is being kept. They can play, and their progress will show here once you give consent.",
    consentAsk:
      "May we keep a record of what your child does in the games — the activities, levels and skills — so you and their teacher can see their progress?",
    consentAskDetail:
      "This is never video, audio or pictures. It is the list of what they played. You can withdraw at any time, and you can ask us to delete it.",
    consentGive: "Yes, keep their progress",
    consentDecline: "Not now",
    consentGrantedOn: "Given on",
    consentUntil: "Kept until",
    consentVerifiedBy: "Confirmed by",
    consentVerifiedPhoneOtp: "the code sent to your phone",
    revoke: "Withdraw consent",
    /**
     * 🔴 THE SENTENCE THAT MAKES THE BUTTON HONEST. Revoking sets a date; it
     * deletes nothing. `resolveRecording` reads it at the START of the next
     * session, and the historical rows go when the retention job runs. A
     * revoke button that says nothing about that is a right that exists on
     * paper — the exact failure P1–P3 are filed against.
     */
    revokeWarning:
      "Withdrawing stops us keeping anything from their next session onwards. What we already hold is removed when our scheduled clean-up next runs — usually within a day. To remove it now, use “Delete this data” below.",
    revoked: "Consent withdrawn",

    /* P1 */
    exportTitle: "Download this data",
    exportHelp:
      "Everything we hold about your child, as a file you can keep or give to someone else. This is the record itself, not a report.",
    exportAction: "Download",
    exportDone: "Downloaded",

    /* P2 */
    eraseTitle: "Delete this data",
    eraseHelp:
      "Removes everything they have done in the games and withdraws your consent, straight away.",
    /**
     * 🔴 SAYS WHAT SURVIVES. The `Player` row stays: it is the school's
     * enrolment record, and deleting it would drop the child off her teacher's
     * roster mid-term. A parent who is not told that will believe we kept
     * something we should not have.
     */
    eraseWhatRemains:
      "Their name, roll number and class stay with the school — that is the school’s own register, not ours to delete. Everything about how they played is removed.",
    eraseConfirm: "Yes, delete it",
    eraseCancel: "Cancel",
    eraseAction: "Delete this data",
    eraseDone: (n: number) => `Deleted. ${String(n)} activities removed.`,

    /*
     * ── which games ─────────────────────────────────────────────────────────
     *
     * 🔴 THE HINT SAYS OUT LOUD THAT NOBODY ELSE IS IN THESE NUMBERS. This tier
     * refuses an overall percentage and every chart on it already carries *"no
     * target and no comparison with other children"*; a table of games is the
     * shape most likely to be READ as a comparison, so it says the opposite
     * before the parent has to wonder.
     */
    gamesTitle: "What they have played",
    gamesHint:
      "Their own rounds, their own right answers, and the level they reached in each game. No target, and no comparison with other children.",

    /* States */
    loadFailed: "Couldn’t load this.",
    sessionExpired:
      "Your sign-in has run out. Sign in again with your phone number.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",
    empty: "Nothing to show yet.",
    emptyNoConsent:
      "Nothing is being kept for this child yet. Give consent to see their progress.",
  },
} as const;

/**
 * 🔴 THE ERROR MESSAGE NAMES THE REAL CAUSE WHEN WE KNOW IT.
 *
 * Unlike the teacher tier — which 401s because staff login does not exist — a
 * parent 401 means their **15-minute access token aged out**, and there is no
 * `POST /auth/refresh` to rotate it (OD-51). "Couldn't load this" would send
 * them looking for a network problem; "sign in again" is the actual next step,
 * and it is the honest description of a gap this unit cannot close.
 */
export function describeError(err: Error | null, lang: Lang): string {
  const t = PARENT_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401) return t.sessionExpired;
  return t.loadFailed;
}

/** The one-word state, and the sentence that goes under it. */
export function consentLabel(status: ConsentStatus, lang: Lang): string {
  const t = PARENT_COPY[lang];
  switch (status) {
    case "GRANTED":
      return t.consentGranted;
    case "REVOKED":
      return t.consentRevoked;
    case "EXPIRED":
      return t.consentExpired;
    default:
      return t.consentPending;
  }
}

/**
 * The pill tone for a consent state.
 *
 * ⚠️ PENDING IS **NEUTRAL**, NOT DANGER. It is the ordinary state — OD-50 kept
 * the consent row unwritten until this unit, so every existing child is pending
 * on the day this ships. A red pill would tell a parent something has gone
 * wrong when nothing has.
 */
export function consentTone(
  status: ConsentStatus,
): "success" | "warning" | "neutral" {
  switch (status) {
    case "GRANTED":
      return "success";
    case "EXPIRED":
      return "warning";
    case "REVOKED":
      return "warning";
    default:
      return "neutral";
  }
}
